import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import styled from 'styled-components';
import { Button, COLOR } from '@dized/ui';
import {getStringFromTO} from '../common/utils/i18nUtils';
import {convertToAO1} from '../common/utils/assetUtils';

const HeaderContainer = styled.div`
  background: linear-gradient(135deg, ${COLOR.TURQUOISE} 0%, #357abd 100%);
  padding: 20px 24px;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
`;

const BackgroundImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url(${props => props.backgroundUrl});
  background-size: cover;
  background-position: center;
  opacity: 0.1;
  filter: blur(2px);
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  position: relative;
  z-index: 2;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  width: 36px;
  height: 36px;
  cursor: pointer;
  color: white;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  margin-right: 16px;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateX(-2px);
  }

  &:active {
    transform: translateX(0);
  }

  &:before {
    content: '←';
    font-family: system-ui, -apple-system, sans-serif;
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  flex: 1;
`;

const BreadcrumbItem = styled.span`
  &:not(:last-child)::after {
    content: '/';
    margin: 0 8px;
    opacity: 0.6;
  }
`;

const BreadcrumbLink = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  text-decoration: none;
  padding: 0;
  font-size: inherit;
  
  &:hover {
    color: white;
    text-decoration: underline;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  position: relative;
  z-index: 2;
`;

const GameImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const GameInfo = styled.div`
  flex: 1;
  color: white;
`;

const GameTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  margin: 0 0 8px 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1.2;
`;

const GameTagline = styled.h2`
  font-size: 1.1rem;
  font-weight: 400;
  margin: 0 0 16px 0;
  opacity: 0.9;
  line-height: 1.3;
`;

const GameMeta = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 0.9rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const MetaIcon = styled.i`
  font-size: 16px;
  margin-right: 4px;
`;

const PublisherInfo = styled.div`
  font-size: 0.95rem;
  opacity: 0.85;
  margin-bottom: 8px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-shrink: 0;
`;

const StyledButton = styled(Button)`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  
  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const DefaultImage = 'https://dummyimage.com/120x120/cccccc/000000&text=No+image+set';

const mapStateToProps = (store) => ({
  games: store.db.games,
});

// Helper functions to reduce complexity
const formatPlayerCount = (playerMin, playerMax) => {
  if (!playerMin && !playerMax) return null;
  if (playerMin === playerMax) return `${playerMin}`;
  if (!playerMax || playerMax === 0) return `${playerMin}+`;
  return `${playerMin}-${playerMax}`;
};

const formatAgeRange = (ageMin, ageMax) => {
  if (!ageMin && !ageMax) return `+0`;
  if (ageMin === ageMax) return `${ageMin}`;
  if (!ageMin && ageMax) return `0-${ageMax}`;
  if (!ageMax || ageMax === 0) return `${ageMin}+`;
  return `${ageMin}-${ageMax}`;
};

const formatPlayingTime = (playingTime) => {
  if (!playingTime) return null;
  return `${playingTime} min`;
};

const renderBreadcrumbs = (projectName, gameTitle, onAllGamesClick, onGameClick) => {
  if (projectName) {
    return (
      <Breadcrumbs>
        <BreadcrumbItem>
          <BreadcrumbLink onClick={onAllGamesClick}>
            All Games
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink onClick={onGameClick}>
            {gameTitle}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>{projectName}</BreadcrumbItem>
      </Breadcrumbs>
    );
  }
  
  return (
    <Breadcrumbs>
      <BreadcrumbItem>
        <BreadcrumbLink onClick={onAllGamesClick}>
          All Games
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbItem>{gameTitle}</BreadcrumbItem>
    </Breadcrumbs>
  );
};

const renderGameMeta = (playerCount, playingTimeFormatted, ageRange, hasRules) => (
  <GameMeta>
    {playerCount && (
      <MetaItem>
        <MetaIcon className="material-icons">people</MetaIcon>
        <span>{playerCount} players</span>
      </MetaItem>
    )}
    {playingTimeFormatted && (
      <MetaItem>
        <MetaIcon className="material-icons">schedule</MetaIcon>
        <span>{playingTimeFormatted}</span>
      </MetaItem>
    )}
    {ageRange && (
      <MetaItem>
        <MetaIcon className="material-icons">cake</MetaIcon>
        <span>{ageRange} years</span>
      </MetaItem>
    )}
  </GameMeta>
);

const renderActionButtons = (children) => {
  if (!children) return null;
  
  return (
    <ActionButtons>
      {React.Children.map(children, child => 
        React.isValidElement(child) ? 
          React.cloneElement(child, {
            ...child.props,
            as: StyledButton
          }) : child
      )}
    </ActionButtons>
  );
};

export const GameHeader = ({
  gameId,
  gameName,
  projectName,
  onBackClick,
  onGameClick,
  onAllGamesClick,
  children,
  games,
}) => {
  const game = games?.gameLookup?.[gameId] || {};
  const {
    assets,
    tagline,
    title,
    player_min: playerMin,
    player_max: playerMax,
    age_min: ageMin,
    age_max: ageMax,
    playing_time: playingTime,
    publishers
  } = game;

  const boxart = assets?.boxart && convertToAO1(assets.boxart);
  const gameTitle = getStringFromTO(title) || gameName || 'Game';
  const gameTagline = getStringFromTO(tagline);
  const publishersList = Array.isArray(publishers) ? publishers : [];
  const publisher = publishersList.length > 0 ? getStringFromTO(publishersList[0]) : '';
  const hasRules = Boolean(game.uuid); // Assume if game exists, rules are available

  const backgroundUrl = boxart?.url && !boxart.url.includes('dummyimage.com') ? boxart.url : null;

  const playerCount = formatPlayerCount(playerMin, playerMax);
  const ageRange = formatAgeRange(ageMin, ageMax);
  const playingTimeFormatted = formatPlayingTime(playingTime);

  return (
    <HeaderContainer>
      <BackgroundImage backgroundUrl={backgroundUrl} />
      
      <HeaderTop>
        <BackButton 
          onClick={onBackClick}
          title={projectName ? 'Back to Game' : 'Back to All Games'}
        />
        {renderBreadcrumbs(projectName, gameTitle, onAllGamesClick, onGameClick)}
      </HeaderTop>
      
      <HeaderContent>
        <GameImage 
          alt="Game Art" 
          src={boxart?.url || DefaultImage} 
        />
        
        <GameInfo>
          <GameTitle>{gameTitle}</GameTitle>
          {gameTagline && <GameTagline>{gameTagline}</GameTagline>}
          {publisher && <PublisherInfo>{publisher}</PublisherInfo>}
          {renderGameMeta(playerCount, playingTimeFormatted, ageRange, hasRules)}
        </GameInfo>
        
        {renderActionButtons(children)}
      </HeaderContent>
    </HeaderContainer>
  );
};

GameHeader.propTypes = {
  gameId: PropTypes.string.isRequired,
  gameName: PropTypes.string,
  projectName: PropTypes.string,
  onBackClick: PropTypes.func.isRequired,
  onGameClick: PropTypes.func,
  onAllGamesClick: PropTypes.func.isRequired,
  children: PropTypes.node,
  games: PropTypes.shape({
    gameLookup: PropTypes.object,
  }),
};

export default connect(mapStateToProps)(GameHeader);
